import React from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { Link } from 'react-router-dom';
import './Dashboard.css';
import PortalHeader from './PortalHeader';

const APP_RELEASE_ITEMS = [
    {
        number: '01',
        title: 'App releases',
        description: 'Review published and staging releases, previous versions, and managed builds.',
        path: '/app-list',
        action: 'Open app releases',
        tone: 'dark',
        icon: 'releases',
    },
];

const DEVELOPER_TOOL_ITEMS = [
    {
        number: '02',
        title: 'App updates',
        description: 'Configure required updates, synchronize version information, and manage release notes.',
        path: '/updates',
        action: 'Open updates',
        tone: 'orange',
        icon: 'updates',
    },
    {
        number: '03',
        title: 'App logs',
        description: 'Investigate support requests using device information and detailed log records.',
        path: '/logs',
        action: 'Open app logs',
        tone: 'blue',
        icon: 'logs',
    },
    {
        number: '04',
        title: 'Android notification icons',
        description: 'Create a monochrome notification icon for Android Studio’s Image Asset Studio.',
        path: '/notification-icon',
        action: 'Create notification icon',
        tone: 'orange',
        icon: 'notification',
    },
    {
        number: '05',
        title: 'Legacy logs',
        description: 'Review historical logs and manage resolved issues.',
        path: '/legacy-logs',
        action: 'Open legacy logs',
        tone: 'neutral',
        icon: 'history',
    },
];

function MenuIcon({ name }) {
    const paths = {
        releases: <><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9M8 5.25l8 4.5" /></>,
        updates: <><path d="M20 8a8 8 0 0 0-13-3L4 8m0-5v5h5M4 16a8 8 0 0 0 13 3l3-3m0 5v-5h-5" /></>,
        logs: <><path d="M5 3h10l4 4v14H5V3Zm10 0v5h4M8 12h8M8 16h5" /></>,
        notification: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /></>,
        history: <><path d="M3 11a9 9 0 1 1 3 7M3 4v7h7M12 7v5l3 2" /></>,
    };

    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

function DashboardMenu({ items, label, featured = false }) {
    return (
        <section
            className={`portal-dashboard-menu${featured ? ' featured' : ''}`}
            aria-label={label}
        >
            {items.map((item) => (
                <Link
                    key={item.path}
                    to={item.path}
                    className={`portal-menu-card ${item.tone}`}
                >
                    <div className="portal-menu-topline" aria-hidden="true">
                        <span className="portal-menu-icon"><MenuIcon name={item.icon} /></span>
                        <span className="portal-menu-number">{item.number}</span>
                    </div>
                    <div className="portal-menu-copy">
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>
                    </div>
                    <span className="portal-menu-action">
                        {item.action}
                        <span aria-hidden="true">→</span>
                    </span>
                </Link>
            ))}
        </section>
    );
}

function Dashboard() {
    const handleLogout = async () => {
        try {
            await signOut(getAuth());
            window.location.href = '/login';
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    return (
        <div className="portal-dashboard">
            <PortalHeader>
                <button type="button" className="portal-dashboard-logout" onClick={handleLogout}>
                    Sign out
                </button>
            </PortalHeader>

            <main className="portal-dashboard-main">
                <section className="portal-dashboard-hero">
                    <p className="portal-dashboard-eyebrow">Internal portal</p>
                    <h1>FieldOps portal</h1>
                    <p>Manage mobile app releases, update requirements, and diagnostic logs.</p>
                </section>

                <section className="portal-dashboard-section" aria-labelledby="app-release-heading">
                    <h2 id="app-release-heading">App releases</h2>
                    <DashboardMenu items={APP_RELEASE_ITEMS} label="App releases" featured />
                </section>

                <section className="portal-dashboard-section" aria-labelledby="developer-tools-heading">
                    <h2 id="developer-tools-heading">Developer tools</h2>
                    <DashboardMenu items={DEVELOPER_TOOL_ITEMS} label="Developer tools" />
                </section>
            </main>
        </div>
    );
}

export default Dashboard;
